import { createPortal } from 'react-dom';
import { X, Users, QrCode } from 'lucide-react';
import { haptic } from '../../utils/haptics';
import { track } from '../../analytics';

interface FlightMatchSheetProps {
  open: boolean;
  onClose: () => void;
  flightNumber: string;
  date: string;
  count: number;
  onShowQR: () => void;
}

export function FlightMatchSheet({ open, onClose, flightNumber, date, count, onShowQR }: FlightMatchSheetProps) {
  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-slate-800 rounded-t-2xl border-t border-slate-700 animate-slide-up"
        style={{ paddingBottom: 'calc(var(--sab, 0px) + 16px)' }}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full bg-slate-600" />
        </div>

        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-slate-700 text-slate-400"
        >
          <X size={16} />
        </button>

        <div className="px-5 pb-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-coral/20 flex items-center justify-center">
              <Users size={16} className="text-coral" />
            </div>
            <span className="text-sm font-bold text-slate-100">Flight Match</span>
          </div>

          <p className="text-sm text-slate-300 mb-1">
            <span className="font-bold text-coral">{count}</span> other crew member{count !== 1 ? 's' : ''} on{' '}
            <span className="font-mono font-bold text-slate-100">{flightNumber}</span> also use{count === 1 ? 's' : ''} Rost & Found.
          </p>
          <p className="text-xs text-slate-500 mb-5">
            Show your QR code at the briefing to connect instantly. Once connected, you'll see each other's layover overlaps.
          </p>

          <button
            onClick={() => {
              haptic();
              track('flight_match_qr_shown', { flight_number: flightNumber, date, count });
              onShowQR();
            }}
            className="w-full py-3 rounded-xl bg-accent text-white text-sm font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
          >
            <QrCode size={16} />
            Show My QR Code
          </button>
        </div>
      </div>
    </div>,
    document.getElementById('app-shell') || document.body,
  );
}
